import { useMyOrders } from "api/Client/product";
import InputSearch, {
  SelectInput,
} from "components/ClientLayoutPages/ClientInputField/InputsFilter";
import Container from "components/ClientLayoutPages/Container/Container";
import MyOrderModal from "components/Modal/Client/MyOrderModal/MyOrderModal";
import SkeletonMyOrder from "components/SkeletonList/SkeletonMyOrder";
import useParam from "Hooks/useParam";
import { useEffect, useState } from "react";
import {
  HiOutlineArrowLongLeft,
  HiOutlineArrowLongRight,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineClipboardDocumentList,
  HiOutlineCube,
  HiOutlineHashtag,
  HiOutlineXCircle,
} from "react-icons/hi2";

const getOrderStateMeta = (state) => {
  if (state === "pending") {
    return {
      label: "انتظار",
      icon: <HiOutlineClock />,
      color: "text-amber-400",
    };
  }

  if (state === "approved" || state === "completed") {
    return {
      label: "مكتملة",
      icon: <HiOutlineCheckCircle />,
      color: "text-emerald-400",
    };
  }

  return {
    label: "مرفوضة",
    icon: <HiOutlineXCircle />,
    color: "text-rose-400",
  };
};

export default function MyOrders() {
  const orderId = useParam("orderId");
  const [openMyOrderModal, setOpenMyOrderModal] = useState(false);
  const [currentId, setCurrentId] = useState(orderId || null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [orderState, setOrderState] = useState("");

  const {
    data: myOrders,
    isFetching: myOrdersIsLoading,
    isSuccess: myOrdersIsSuccess,
  } = useMyOrders(18, pageNumber, searchValue, orderState);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageNumber]);

  useEffect(() => {
    if (orderId) {
      setOpenMyOrderModal(true);
    }
  }, [orderId]);

  return (
    <Container title="طلباتي">
      <div className="portal-page-card mx-auto mb-8 mt-4 grid max-w-2xl grid-cols-1 gap-4 rounded-[1.5rem] p-4 md:grid-cols-2">
        <InputSearch
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setPageNumber={setPageNumber}
        />
        <SelectInput
          value={orderState}
          setValue={setOrderState}
          setPageNumber={setPageNumber}
          options={[
            { value: "completed", label: "مكتمل" },
            { value: "canceled", label: "مرفوضة" },
            { value: "pending", label: "انتظار" },
          ]}
        />
      </div>

      {myOrdersIsLoading ? <SkeletonMyOrder number={6} /> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {!myOrdersIsLoading &&
          myOrdersIsSuccess &&
          myOrders.data?.orders.length > 0 &&
          myOrders.data.orders.map((item) => {
            const stateMeta = getOrderStateMeta(item.order_state);

            return (
              <div
                key={item.id}
                onClick={() => {
                  setCurrentId(item.id);
                  setOpenMyOrderModal(true);
                }}
                className="portal-page-card card-animation grid h-auto cursor-pointer gap-2.5 rounded-[1.35rem] p-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold sm:text-sm ${stateMeta.color}`}
                  >
                    <span className="text-lg">{stateMeta.icon}</span>
                    {stateMeta.label}
                  </div>
                  <h2
                    className="text-lg font-bold portal-accent sm:text-xl"
                    style={{ direction: "ltr" }}
                  >
                    {Number(Math.abs(item.total)).toLocaleString()}{" "}
                    <span className="text-sm">{item.currency}</span>
                  </h2>
                </div>

                <InfoRow
                  icon={
                    <HiOutlineClipboardDocumentList className="text-orange-400" />
                  }
                  label="نوع العملية"
                  value="شراء منتج"
                />
                <InfoRow
                  icon={<HiOutlineHashtag className="text-amber-400" />}
                  label="رقم الطلب"
                  value={item.id}
                />
                <InfoRow
                  icon={<HiOutlineCube className="text-orange-300" />}
                  label="الخدمة"
                  value={item.service}
                  clamp
                />
                <InfoRow
                  icon={<HiOutlineCalendarDays className="text-orange-400" />}
                  label="التاريخ"
                  value={item.created_at}
                  ltr
                />
              </div>
            );
          })}
      </div>

      <Pagination
        current={myOrders?.data?.pagination?.current_page || 0}
        total={myOrders?.data?.pagination?.last_page || 0}
        onPrev={() => setPageNumber((prev) => prev - 1)}
        onNext={() => setPageNumber((prev) => prev + 1)}
        disablePrev={pageNumber === 1 || myOrdersIsLoading}
        disableNext={
          myOrders?.data?.orders.length === 0 ||
          pageNumber >= myOrders?.data.pagination?.last_page ||
          myOrders?.data?.pagination?.total === 0 ||
          myOrdersIsLoading
        }
      />

      {openMyOrderModal ? (
        <MyOrderModal
          isOpen={openMyOrderModal}
          toggle={setOpenMyOrderModal}
          orderId={currentId}
          url={orderId}
        />
      ) : null}
    </Container>
  );
}

function InfoRow({ icon, label, value, clamp = false, ltr = false }) {
  return (
    <div
      className="flex items-start gap-2.5"
      style={{ color: "var(--layout-text-muted)" }}
    >
      <div className="text-lg">{icon}</div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-semibold portal-accent">{label}</span>
        <span
          className={`text-sm font-bold sm:text-[0.95rem] ${clamp ? "line-clamp-2" : ""}`}
          style={{
            direction: ltr ? "ltr" : "rtl",
            color: "var(--layout-text)",
          }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function Pagination({
  current,
  total,
  onPrev,
  onNext,
  disablePrev,
  disableNext,
}) {
  return (
    <div className="mt-16 flex w-full flex-col items-center">
      <span className="flex gap-1 text-sm portal-accent">
        <span className="font-semibold">{current}</span>
        <span className="font-medium">من أصل</span>
        <span className="font-semibold">{total}</span>
      </span>
      <div className="mt-3 flex gap-4">
        <button
          onClick={onPrev}
          disabled={disablePrev}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          <HiOutlineArrowLongRight />
          السابق
        </button>
        <button
          onClick={onNext}
          disabled={disableNext}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          التالي
          <HiOutlineArrowLongLeft />
        </button>
      </div>
    </div>
  );
}
